import React, { useState, useEffect, useRef } from 'react';
import { User, Mail, Phone, Lock, Eye, EyeOff, Save, ShieldCheck, Check, Calendar, Camera } from 'lucide-react';
import toast from 'react-hot-toast';
import api, { getStorageUrl } from '../../api/axios';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import styles from './Profile.module.css';

const Profile = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [user, setUser] = useState(() => {
    // Initialize from localStorage for instant feedback
    const savedUser = JSON.parse(localStorage.getItem('user') || '{}');
    return {
      name: savedUser.name || '',
      email: savedUser.email || '',
      mobile: savedUser.mobile || '',
      role: savedUser.role || 'Project Manager',
      joined_at: savedUser.joined_at || 'March 2024'
    };
  });

  const [passwords, setPasswords] = useState({
    current_password: '',
    password: '',
    password_confirmation: ''
  });

  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Image Upload State
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/user');
      if (response.data.success) {
        const fetchedData = response.data.data;
        setUser(prevUser => {
          const updated = {
            ...prevUser,
            ...fetchedData,
            joined_at: fetchedData.joined_at || prevUser.joined_at,
            role: fetchedData.role || 'Project Manager',
            profile_image: fetchedData.profile_image || null
          };
          localStorage.setItem('user', JSON.stringify(updated));
          return updated;
        });
      }
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      toast.error('Could not load profile information.');
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }

      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('name', user.name);
      formData.append('email', user.email);
      formData.append('mobile', user.mobile);

      if (selectedFile) {
        formData.append('profile_image', selectedFile);
      }

      // Add _method=PUT for Laravel if sending as POST but wanting PUT behavior with files
      // response = await api.post('/profile', formData, { ... });
      // But standard PUT should work if backend is configured. 
      // Laravel often requires POST with _method=PUT for multipart/form-data.
      formData.append('_method', 'PUT');

      const response = await api.post('/profile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        toast.success('Profile updated successfully!');
        // Update local state and local storage with fresh data from backend
        const updatedUser = { ...user, ...response.data.data };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setSelectedFile(null);
        setImagePreview(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwords.password !== passwords.password_confirmation) {
      toast.error('New passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/change-password', passwords);
      if (response.data.success) {
        toast.success('Password changed successfully!');
        setPasswords({
          current_password: '',
          password: '',
          password_confirmation: ''
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${styles['profile-layout-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={toggleSidebar} />

        <main className={styles['main-content']}>
          <div className={styles['profile-container']}>

            {/* Minimalist Professional Header Section */}
            <div className={styles['profile-header-card']}>
              <div className={styles['avatar-container']} onClick={handleImageClick}>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageChange}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
                <img
                  src={imagePreview || (user.profile_image ? getStorageUrl(user.profile_image) : `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || 'User')}&background=fff&color=f97316&size=120&bold=true`)}
                  alt="Profile"
                  className={styles['avatar']}
                />
                <div className={styles['upload-overlay']}>
                  <Camera size={24} className={styles['camera-icon']} />
                  <span className={styles['upload-text-simple']}>Update</span>
                </div>
                <div className={styles['verified-badge']}>
                  <ShieldCheck size={20} />
                </div>
              </div>

              <div className={styles['user-main-info']}>
                <div className={styles['user-name-wrapper']}>
                  <h1>{user.name || 'Loading...'}</h1>
                  <div className={styles['header-badges']}>
                    <span className={styles['status-badge']}>Active</span>
                    <span className={styles['role-badge']}>
                      {user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'User'}
                    </span>
                  </div>
                </div>

                <div className={styles['profile-stats']}>
                  <div className={styles['stat-item']}>
                    <Mail size={14} />
                    <span>{user.email || 'Email missing'}</span>
                  </div>
                  <div className={styles['stat-item']}>
                    <Phone size={14} />
                    <span>{user.mobile || 'No mobile'}</span>
                  </div>
                  <div className={styles['stat-item']}>
                    <Calendar size={14} />
                    <span>Joined {user.joined_at || 'March 2024'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles['profile-grid']}>

              {/* Edit Profile Card */}
              <div className={styles['card']}>
                <div className={styles['card-title']}>
                  <User size={22} />
                  <span>Personal Information</span>
                </div>

                <form onSubmit={handleProfileUpdate}>
                  <div className={styles['form-grid']}>
                    <div className={styles['form-group']}>
                      <label className={styles['label']}>Full Name <span className={styles['required-star']}>*</span></label>
                      <div className={styles['input-wrapper']}>
                        <User size={18} className={styles['input-icon']} />
                        <input
                          type="text"
                          className={styles['input']}
                          value={user.name}
                          onChange={(e) => setUser({ ...user, name: e.target.value })}
                          required
                          minLength={2}
                          maxLength={200}
                        />
                      </div>
                    </div>

                    <div className={styles['form-group']}>
                      <label className={styles['label']}>Mobile Number <span className={styles['required-star']}>*</span></label>
                      <div className={styles['input-wrapper']}>
                        <Phone size={18} className={styles['input-icon']} />
                        <input
                          type="tel"
                          className={styles['input']}
                          value={user.mobile}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            setUser({ ...user, mobile: value });
                          }}
                          required
                          pattern="[0-9]{10,12}"
                          title="Mobile number should be 10 to 12 digits"
                        />
                      </div>
                    </div>

                    <div className={`${styles['form-group']} ${styles['full-width']}`}>
                      <label className={styles['label']}>Email Address <span className={styles['required-star']}>*</span></label>
                      <div className={styles['input-wrapper']}>
                        <Mail size={18} className={styles['input-icon']} />
                        <input
                          type="email"
                          className={styles['input']}
                          value={user.email}
                          onChange={(e) => setUser({ ...user, email: e.target.value })}
                          required
                          maxLength={200}
                        />
                      </div>
                    </div>
                  </div>

                  <button type="submit" className={styles['save-btn']} disabled={loading}>
                    {loading ? 'Saving...' : 'Update Profile'}
                  </button>
                </form>
              </div>

              {/* Change Password Card */}
              <div className={styles['card']}>
                <div className={styles['card-title']}>
                  <ShieldCheck size={22} />
                  <span>Security</span>
                </div>

                <form onSubmit={handlePasswordChange}>
                  <div className={styles['form-group']}>
                    <label className={styles['label']}>Current Password <span className={styles['required-star']}>*</span></label>
                    <div className={styles['input-wrapper']}>
                      <Lock size={18} className={styles['input-icon']} />
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        className={styles['input']}
                        value={passwords.current_password}
                        onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })}
                        required
                      />
                      <button
                        type="button"
                        className={styles['password-toggle']}
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      >
                        {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div className={styles['form-group']}>
                    <label className={styles['label']}>New Password <span className={styles['required-star']}>*</span></label>
                    <div className={styles['input-wrapper']}>
                      <Lock size={18} className={styles['input-icon']} />
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        className={styles['input']}
                        value={passwords.password}
                        onChange={(e) => setPasswords({ ...passwords, password: e.target.value })}
                        required
                        minLength={8}
                      />
                      <button
                        type="button"
                        className={styles['password-toggle']}
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div className={styles['form-group']}>
                    <label className={styles['label']}>Confirm New Password <span className={styles['required-star']}>*</span></label>
                    <div className={styles['input-wrapper']}>
                      <Lock size={18} className={styles['input-icon']} />
                      <input
                        type="password"
                        className={styles['input']}
                        value={passwords.password_confirmation}
                        onChange={(e) => setPasswords({ ...passwords, password_confirmation: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <button type="submit" className={styles['save-btn']} disabled={loading}>
                    {loading ? 'Changing...' : 'Change Password'}
                  </button>
                </form>
              </div>
            </div>

          </div>
        </main>
      </div>
    </div>
  );
};

export default Profile;
